import type { FC, ReactNode } from "react"
import { useRef, useState } from "react"

import { useRecoilValue } from "recoil"

import useUpdateProposalMargin from "~/api/hooks/useUpdateProposalMargin"
import { SimpleNumberInputInline } from "~/components/Input/SimpleNumberInput"
import {
  NOTIFICATION_COLORS,
  NotificationMessage,
} from "~/containers/Proposal/state/notifications/notifications"
import {
  useProposalActions,
  useProposalContext,
} from "~/containers/Proposal/state/proposal/proposal-context"
import { currentPermissionsState } from "~/containers/Proposal/state/user/user-state"
import { Permission } from "~/graphql/client"
import { VISION_DESIGN_PERMISSION } from "~/utils/canManipulateDesign"
import { PricingSummaryLineItemType } from "~/utils/finance/pricing-summary"

import {
  ExciseTaxRow,
  PricingEquipmentItemRow,
  PricingItemRow,
  PricingTableContainer,
  SalesTaxRow,
} from "./SystemPricingShared"
import type { PricingTableProps } from "./systemPricingTypes"
import { formatters, validateMarginCosts } from "./utils"
import type { SystemPricingItem } from "./utils/system-pricings.utils"
import { filterAdderItems } from "./utils/system-pricings.utils"

interface EnterpriseSystemPricingProps extends PricingTableProps {
  isBaseCost: boolean
}

const EnterpriseSystemPricing: FC<PricingTableProps> = ({
  maxMargin,
  minimumMargin,
  proposalId,
  proposalPrice,
  isPowurLine = false,
}: PricingTableProps): ReactNode => {
  const { local: proposal } = useProposalContext()
  const marginVisibility: string = proposal.enterprise?.marginVisibility ?? ``
  const isBaseCost: boolean = marginVisibility === `baseCost`
  const enterpriseHasHoldback: boolean =
    (proposal.enterpriseSettings?.orgHoldbackPpw ?? 0) > 0

  if (isPowurLine) {
    return (
      <EnterpriseSystemPricingPowurLine
        maxMargin={maxMargin}
        minimumMargin={minimumMargin}
        proposalId={proposalId}
        variant="enterprise"
        proposalPrice={proposalPrice}
      />
    )
  }

  if (!enterpriseHasHoldback) {
    return (
      <EnterpriseSystemPricingNoHoldBack
        maxMargin={maxMargin}
        minimumMargin={minimumMargin}
        proposalId={proposalId}
        variant="enterprise"
        isPowurLine={isPowurLine}
        isBaseCost={isBaseCost}
        proposalPrice={proposalPrice}
      />
    )
  }

  return (
    <EnterpriseSystemPricingBaseCostWithHoldBack
      maxMargin={maxMargin}
      minimumMargin={minimumMargin}
      proposalId={proposalId}
      variant="enterprise"
      isPowurLine={isPowurLine}
      proposalPrice={proposalPrice}
    />
  )
}

const EnterpriseSystemPricingPowurLine: FC<PricingTableProps> = ({
  maxMargin,
  minimumMargin,
  proposalId,
  proposalPrice,
}: PricingTableProps): ReactNode => {
  const { local: proposal, changeLocal } = useProposalContext()
  const currentPermissions = useRecoilValue(currentPermissionsState)
  const updateProposal = useUpdateProposalMargin()

  // Role gating for PowurLine enterprise fields
  const isEnterpriseOwnerOrManager = currentPermissions.some((permission) =>
    [
      Permission.enterprise_admin,
      Permission.enterprise_manager,
      Permission.enterprise_owner,
    ].includes(permission as Permission),
  )

  const isAdmin = currentPermissions.some(
    (permission) =>
      permission === Permission.vision_admin ||
      permission === Permission.vision_proposal_admin ||
      permission === Permission.vision_developer,
  )

  const shouldShowEnterpriseFields = isEnterpriseOwnerOrManager || isAdmin

  const { powurLinePricing } = proposalPrice

  const sellerPowurLinePricePerWatt = powurLinePricing?.sellerPowurLinePPW ?? 0

  const enterprisePowurLinePricePerWatt =
    powurLinePricing?.enterpriseBaselinePPW ?? 0

  const sellerPowurLinePricing = powurLinePricing?.sellerPowurLinePrice ?? 0

  const enterpriseCommissionPricePerWatt =
    sellerPowurLinePricePerWatt - enterprisePowurLinePricePerWatt

  const enterpriseCommission = powurLinePricing?.enterpriseCommission ?? 0
  const enterprisePowurLinePricing =
    powurLinePricing?.enterprisePowurLinePrice ?? 0

  const isManager = currentPermissions.some(
    (permission) =>
      permission === VISION_DESIGN_PERMISSION.enterprise_admin ||
      permission === VISION_DESIGN_PERMISSION.enterprise_manager ||
      permission === Permission.enterprise_owner,
  )

  // Equipment should mirror fixed holdback visibility for PowurLine
  const equipmentItems: SystemPricingItem[] = proposalPrice.lineItems.filter(
    (item) => item.type === PricingSummaryLineItemType.EQUIPMENT,
  )

  const shouldShowEquipmentTable = equipmentItems.length > 0

  // Split line items into adders only - include negative rebates
  const adderItems = filterAdderItems(proposalPrice.lineItems).filter(
    (item) => item.total !== 0,
  )
  const totalAdders = adderItems.reduce((sum, item) => sum + item.total, 0)

  // Margin state management - store full precision, do NOT round for state
  const [userAdjustedMargin, setUserAdjustedMargin] = useState<number | null>(
    proposalPrice.marginRate,
  )

  const [loadingState, setLoadingState] = useState<`idle` | `margin`>(`idle`)

  const handleMarginUpdateBlur = async (
    newMargin: number | null,
  ): Promise<void> => {
    if (typeof newMargin !== `number` || isNaN(newMargin)) {
      return
    }

    // Use tolerance-based comparison to handle floating point precision
    const MARGIN_TOLERANCE = 0.000001
    if (Math.abs(newMargin - proposalPrice.marginRate) < MARGIN_TOLERANCE) {
      return
    }

    const marginErrorMessage = validateMarginCosts(
      newMargin,
      minimumMargin,
      maxMargin,
    )
    if (marginErrorMessage) {
      console.error(`failed to update margin ${marginErrorMessage}`)
      return
    }

    try {
      setLoadingState(`margin`)
      await updateProposal.mutateAsync({
        id: proposalId,
        margin: newMargin,
      })
      setLoadingState(`idle`)

      changeLocal({ margin: newMargin })
    } catch (error) {
      setLoadingState(`idle`)
      console.error(`Failed to fetch pricing for new margin:`, error)
    }
  }

  return (
    <>
      {shouldShowEquipmentTable && (
        <div className="mb-2 rounded-tl-lg rounded-tr-lg bg-gray-100 p-0.5">
          <div className="overflow-y-auto bg-white">
            <table className="w-full table-fixed border-separate border-spacing-y-0 border-b-0">
              <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
                <tr className="text-sm font-extralight text-gray-500">
                  <th className="px-6 py-2 text-left">Equipment</th>
                  <th className="px-6 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="table-border-show divide-y divide-gray-200">
                {equipmentItems.map((item: SystemPricingItem) => (
                  <PricingEquipmentItemRow
                    key={item.id}
                    item={item}
                    excludeTotal={true}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adder Section */}
      {adderItems.length > 0 && (
        <div className={adderItems.length > 0 ? `mt-6` : ``}>
          <PricingTableContainer>
            <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
              <tr className="text-sm font-extralight text-gray-500">
                <th className="px-6 py-2 text-left">Adder</th>
                <th className="px-6 py-2 text-end">Price/Watt</th>
                <th className="px-6 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="table-border-show divide-y divide-gray-200">
              {adderItems.map((item: SystemPricingItem) => (
                <PricingItemRow key={`adder-${item.id}`} item={item} />
              ))}
            </tbody>
          </PricingTableContainer>
        </div>
      )}

      {/* Cost Section - always shown for all users */}
      <div className={adderItems.length > 0 ? `mt-6` : ``}>
        <PricingTableContainer>
          <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
            <tr className="text-sm font-extralight text-gray-500">
              <th className="px-6 py-2 text-left">Cost</th>
              <th className="px-6 py-2 text-end">Price/Watt</th>
              <th className="px-6 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="table-border-show divide-y divide-gray-200">
            {/* Seller PowurLine Price Row */}
            <tr className="bg-white">
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                PowurLine
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                {formatters.pricePerWatt(sellerPowurLinePricePerWatt)}
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                {formatters.currency(sellerPowurLinePricing)}
              </td>
            </tr>

            {/* Total Adders Row - always show, even if $0.00 */}
            <tr className="bg-white">
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                Total Adders
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                {formatters.pricePerWatt(totalAdders / proposalPrice.systemSize)}
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                {formatters.currency(totalAdders)}
              </td>
            </tr>

            {/* Sales Tax Row */}
            <SalesTaxRow
              salesTax={proposalPrice.salesTax}
              systemSizeInWatts={proposalPrice.systemSize}
            />

            {/* Margin Row - editable $/W */}
            <tr key="margin" className="transition-colors hover:bg-gray-50">
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                Margin
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-center text-sm text-gray-600">
                <SimpleNumberInputInline
                  value={userAdjustedMargin}
                  setValue={setUserAdjustedMargin}
                  min={0}
                  onBlur={handleMarginUpdateBlur}
                  loading={loadingState === `margin`}
                />
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                {formatters.currency(
                  (userAdjustedMargin ?? 0) * proposalPrice.systemSize,
                )}
              </td>
            </tr>

            {/* Excise Tax Row - only if present */}
            <ExciseTaxRow
              exciseTaxRate={proposalPrice.generalExciseTaxRate}
              exciseTax={proposalPrice.generalExciseTax}
            />

            {/* Total Cost Row */}
            <tr key="total-cost" className="bg-white">
              <td className="border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                Total Cost
              </td>
              <td className="border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                {formatters.pricePerWatt(
                  proposalPrice.total / proposalPrice.systemSize,
                )}
              </td>
              <td className="border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                {formatters.currency(proposalPrice.total)}
              </td>
            </tr>
          </tbody>
          <tfoot className="sticky bottom-0 z-20 bg-white"></tfoot>
        </PricingTableContainer>
      </div>

      {/* Enterprise Info table (separate) for enterprise users */}
      {shouldShowEnterpriseFields && (
        <div className="mt-6">
          <PricingTableContainer>
            <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
              <tr className="text-sm font-extralight text-gray-500">
                <th className="px-6 py-2 text-left">Enterprise Info</th>
                <th className="px-6 py-2 text-end">Price/Watt</th>
                <th className="px-6 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="table-border-show divide-y divide-gray-200">
              <tr className="bg-white">
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                  Enterprise PowurLine Price
                </td>
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                  {formatters.pricePerWatt(enterprisePowurLinePricePerWatt)}
                </td>
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                  {formatters.currency(enterprisePowurLinePricing)}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                  Enterprise Commission
                </td>
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                  {formatters.pricePerWatt(enterpriseCommissionPricePerWatt)}
                </td>
                <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                  {formatters.currency(enterpriseCommission)}
                </td>
              </tr>
            </tbody>
          </PricingTableContainer>
        </div>
      )}
    </>
  )
}

const EnterpriseSystemPricingNoHoldBack: FC<EnterpriseSystemPricingProps> = ({
  maxMargin,
  minimumMargin,
  proposalId,
  isBaseCost,
  proposalPrice,
}: EnterpriseSystemPricingProps): ReactNode => {
  const updateProposal = useUpdateProposalMargin()

  // Shared state and hooks
  const baseCostItem = useRef<SystemPricingItem>({
    id: 1,
    name: `Project Base Cost`,
    type: `baseCost`,
    price: 0,
    total: 0,
    required: true,
    qty: 1,
    isBaseCost: false,
  })

  // Store full precision margin - do NOT round for state
  const [userAdjustedMargin, setUserAdjustedMargin] = useState<number | null>(
    proposalPrice.marginRate,
  )

  const [currentTotalCost, setCurrentTotalCost] = useState<number | null>(
    proposalPrice.total,
  )

  const [loadingState, setLoadingState] = useState<
    `idle` | `margin` | `totalCost`
  >(`idle`)

  const { changeLocal } = useProposalActions()

  // Conditional hooks for base cost mode
  const currentPermissions = useRecoilValue(currentPermissionsState)

  const isManager = currentPermissions.some(
    (permission) =>
      permission === VISION_DESIGN_PERMISSION.enterprise_admin ||
      permission === VISION_DESIGN_PERMISSION.vision_admin ||
      permission === VISION_DESIGN_PERMISSION.enterprise_manager,
  )

  const marginError = validateMarginCosts(
    userAdjustedMargin ?? proposalPrice.marginRate,
    minimumMargin,
    maxMargin,
  )

  // Shared margin update handler
  const handleMarginUpdateBlur = async (
    newMargin: number | null,
  ): Promise<void> => {
    if (typeof newMargin !== `number` || isNaN(newMargin)) {
      return
    }

    // Use tolerance-based comparison to handle floating point precision
    const MARGIN_TOLERANCE = 0.000001
    if (Math.abs(newMargin - proposalPrice.marginRate) < MARGIN_TOLERANCE) {
      return
    }

    const marginErrorMessage = validateMarginCosts(
      newMargin,
      minimumMargin,
      maxMargin,
    )

    if (marginErrorMessage) {
      console.error(`failed to update margin ${marginErrorMessage}`)
      return
    }

    try {
      setLoadingState(`margin`)
      const pricing = await updateProposal.mutateAsync({
        id: proposalId,
        margin: newMargin,
      })
      setLoadingState(`idle`)

      setCurrentTotalCost(pricing.totalContractPrice)
      changeLocal({ margin: newMargin })
    } catch (error) {
      setLoadingState(`idle`)
      console.error(`Failed to fetch pricing for new margin:`, error)
    }
  }

  // Combined filtering logic
  const filterDisplayItems = (
    items: SystemPricingItem[],
  ): SystemPricingItem[] => {
    const displayItems = items.filter((item) => item.total > 0)

    // Base cost specific filtering
    if (isBaseCost) {
      if (!isManager) {
        const baseCostItems: SystemPricingItem[] = displayItems.filter(
          (item) => item.isBaseCost,
        )
        const nonBaseCostItems: SystemPricingItem[] = displayItems.filter(
          (item) => !item.isBaseCost,
        )

        // Calculate base cost
        const baseCostItemsTotal: number = baseCostItems.reduce((acc, item) => {
          return acc + item.total
        }, 0)

        const baseCostItemsPrice: number =
          baseCostItemsTotal / proposalPrice.systemSize

        baseCostItem.current = {
          id: 1,
          name: `Project Base Cost`,
          type: `baseCost`,
          price: baseCostItemsPrice,
          total: baseCostItemsTotal,
          isBaseCost: false,
          required: true,
          qty: 1,
        }

        return nonBaseCostItems
      }
    }

    return displayItems
  }

  const displayItems: SystemPricingItem[] = filterDisplayItems(
    proposalPrice.lineItems,
  )
  const equipmentItems: SystemPricingItem[] = isBaseCost
    ? proposalPrice.lineItems.filter(
        (item) => item.type === PricingSummaryLineItemType.EQUIPMENT,
      )
    : []
  const shouldShowBaseCostRow = isBaseCost && !isManager
  const shouldShowEquipmentTable = isBaseCost && equipmentItems.length > 0

  return (
    <>
      <NotificationMessage
        {...NOTIFICATION_COLORS.danger}
        style={{
          opacity: marginError ? 1 : 0,
          transition: `opacity 0.2s ease`,
          pointerEvents: marginError ? `auto` : `none`,
          marginBottom: `.5rem`,
        }}
      >
        <p>{marginError ?? `\u00A0`}</p>
      </NotificationMessage>

      {shouldShowEquipmentTable && (
        <div className="mb-2 rounded-tl-lg rounded-tr-lg bg-gray-100 p-0.5">
          <div className="overflow-y-auto bg-white">
            <table className="w-full table-fixed border-separate border-spacing-y-0 border-b-0">
              <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
                <tr className="text-sm font-extralight text-gray-500">
                  <th className="px-6 py-2 text-left">Equipment</th>
                  <th className="px-6 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="table-border-show divide-y divide-gray-200">
                {equipmentItems.map((item: SystemPricingItem) => (
                  <PricingEquipmentItemRow
                    key={item.id}
                    item={item}
                    excludeTotal={!isManager}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PricingTableContainer>
        <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
          <tr className="text-sm font-extralight text-gray-500">
            <th className="px-6 py-2 text-left">Item</th>
            <th className="px-6 py-2 text-end">Price/Watt</th>
            <th className="px-6 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="table-border-show divide-y divide-gray-200">
          {displayItems.map((item: SystemPricingItem) => (
            <PricingItemRow key={item.id} item={item} />
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-20 bg-white">
          {shouldShowBaseCostRow && (
            <tr key={baseCostItem.current.id} className="bg-white">
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
                {baseCostItem.current.name}
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
                {formatters.pricePerWatt(baseCostItem.current.price)}
              </td>
              <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
                {formatters.currency(baseCostItem.current.total)}
              </td>
            </tr>
          )}
          <tr key="margin" className="transition-colors hover:bg-gray-50">
            <td
              className={`border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800 ${shouldShowBaseCostRow ? `border-t-0` : ``}`}
            >
              Margin
            </td>
            <td
              className={`border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-center text-sm text-gray-600 ${shouldShowBaseCostRow ? `border-t-0` : ``}`}
            >
              <SimpleNumberInputInline
                value={userAdjustedMargin}
                setValue={setUserAdjustedMargin}
                min={0}
                onBlur={handleMarginUpdateBlur}
                loading={loadingState === `margin`}
              />
            </td>
            <td
              className={`border border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800 ${shouldShowBaseCostRow ? `border-t-0` : ``}`}
            >
              {formatters.currency(
                proposalPrice.marginRate * proposalPrice.systemSize,
              )}
            </td>
          </tr>
          <SalesTaxRow
            salesTax={proposalPrice.salesTax}
            systemSizeInWatts={proposalPrice.systemSize}
          />
          <ExciseTaxRow
            exciseTaxRate={proposalPrice.generalExciseTaxRate}
            exciseTax={proposalPrice.generalExciseTax}
          />
          <tr key="total-cost" className="bg-white">
            <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
              Total Cost
            </td>
            <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
              {formatters.pricePerWatt(
                proposalPrice.total / proposalPrice.systemSize,
              )}
            </td>
            <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
              {formatters.currency(currentTotalCost || 0)}
            </td>
          </tr>
        </tfoot>
      </PricingTableContainer>
    </>
  )
}

const EnterpriseSystemPricingBaseCostWithHoldBack: FC<PricingTableProps> = ({
  maxMargin,
  minimumMargin,
  proposalId,
  proposalPrice,
}): ReactNode => {
  const updateProposal = useUpdateProposalMargin()

  const baseCostItem = useRef<SystemPricingItem>({
    id: 1,
    name: `Project Base Cost`,
    type: `baseCost`,
    price: 0,
    total: 0,
    required: true,
    qty: 1,
    isBaseCost: false,
  })
  // Store full precision margin - do NOT round for state
  const [userAdjustedMargin, setUserAdjustedMargin] = useState<number | null>(
    proposalPrice.marginRate,
  )

  const [currentTotalCost, setCurrentTotalCost] = useState<number | null>(
    proposalPrice.total,
  )

  const [loadingState, setLoadingState] = useState<
    `idle` | `margin` | `totalCost`
  >(`idle`)

  const { local: proposal, changeLocal } = useProposalContext()

  const currentPermissions = useRecoilValue(currentPermissionsState)
  const marginError = validateMarginCosts(
    userAdjustedMargin ?? proposal.margin,
    minimumMargin,
    maxMargin,
  )

  const isManager = currentPermissions.some(
    (permission) =>
      permission === VISION_DESIGN_PERMISSION.enterprise_admin ||
      permission === VISION_DESIGN_PERMISSION.vision_admin ||
      permission === VISION_DESIGN_PERMISSION.enterprise_manager ||
      permission === Permission.enterprise_owner,
  )

  const handleMarginUpdateBlur = async (
    newMargin: number | null,
  ): Promise<void> => {
    if (typeof newMargin !== `number` || isNaN(newMargin)) {
      return
    }

    // Use tolerance-based comparison to handle floating point precision
    const MARGIN_TOLERANCE = 0.000001
    if (Math.abs(newMargin - proposalPrice.marginRate) < MARGIN_TOLERANCE) {
      return
    }

    const marginErrorMessage = validateMarginCosts(
      newMargin,
      minimumMargin,
      maxMargin,
    )
    if (marginErrorMessage) {
      console.error(`failed to update margin ${marginErrorMessage}`)
      return
    }

    try {
      setLoadingState(`margin`)
      const pricing = await updateProposal.mutateAsync({
        id: proposalId,
        margin: newMargin,
      })
      setLoadingState(`idle`)

      setCurrentTotalCost(pricing.totalContractPrice)

      changeLocal({ margin: newMargin })
    } catch (error) {
      setLoadingState(`idle`)
      console.error(`Failed to fetch pricing for new margin:`, error)
    }
  }

  const filterDisplayItems = (
    items: SystemPricingItem[],
  ): SystemPricingItem[] => {
    const displayItems = items.filter(
      (item: SystemPricingItem): boolean => item.total > 0,
    )

    if (isManager) {
      const enterpriseServiceFeeIndex = displayItems.findIndex(
        (item) => item.name === `Enterprise Service Fee`,
      )

      if (enterpriseServiceFeeIndex >= 0) {
        displayItems[enterpriseServiceFeeIndex].isBaseCost = false
      }
    }

    const baseCostItems = displayItems.filter(
      (item: SystemPricingItem): boolean => item.isBaseCost,
    )
    const nonBaseCostItems = displayItems.filter(
      (item: SystemPricingItem): boolean => !item.isBaseCost,
    )

    const baseCostItemsTotal = baseCostItems.reduce(
      (acc: number, item: SystemPricingItem): number => {
        return acc + item.total
      },
      0,
    )

    const baseCostItemsPrice = baseCostItemsTotal / proposalPrice.systemSize

    const baseCost: SystemPricingItem = {
      id: 1,
      name: `Project Base Cost`,
      type: `baseCost`,
      price: baseCostItemsPrice,
      total: baseCostItemsTotal,
      isBaseCost: false,
      required: true,
      qty: 1,
    }

    baseCostItem.current = baseCost

    return nonBaseCostItems
  }

  const displayItems = filterDisplayItems(proposalPrice.lineItems)
  const equipmentItems = proposalPrice.lineItems.filter(
    (item: SystemPricingItem): boolean =>
      item.type === PricingSummaryLineItemType.EQUIPMENT,
  )

  return (
    <>
      <NotificationMessage
        {...NOTIFICATION_COLORS.danger}
        style={{
          opacity: marginError ? 1 : 0,
          transition: `opacity 0.2s ease`,
          pointerEvents: marginError ? `auto` : `none`,
          marginBottom: `.5rem`,
        }}
      >
        <p>{marginError ?? `\u00A0`}</p>
      </NotificationMessage>
      {isManager ? (
        <div className="mb-2 rounded-tl-lg rounded-tr-lg bg-gray-100 p-0.5">
          <div className="overflow-y-auto bg-white">
            <table className="w-full table-fixed border-separate border-spacing-y-0 border-b-0">
              <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
                <tr className="text-sm font-extralight text-gray-500">
                  <th className="px-6 py-2 text-left">Equipment</th>
                  <th className="px-6 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="table-border-show divide-y divide-gray-200">
                {equipmentItems.map((item: SystemPricingItem) => (
                  <PricingEquipmentItemRow
                    key={item.id}
                    item={item}
                    excludeTotal={true}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <PricingTableContainer>
        <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
          <tr className="text-sm font-extralight text-gray-500">
            <th className="px-6 py-2 text-left">Item</th>
            <th className="px-6 py-2 text-end">Price/Watt</th>
            <th className="px-6 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="table-border-show divide-y divide-gray-200">
          {displayItems.map((item: SystemPricingItem) => (
            <PricingItemRow key={item.id} item={item} />
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-20 bg-white">
          <tr key={baseCostItem.current.id} className="bg-white">
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800`}
            >
              {baseCostItem.current.name}
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600`}
            >
              {formatters.pricePerWatt(baseCostItem.current.price)}
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
            >
              {formatters.currency(baseCostItem.current.total)}
            </td>
          </tr>
          <tr key={`margin`} className="transition-colors hover:bg-gray-50">
            <td className="text-gray-80 border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium">
              Margin
            </td>
            <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-center text-sm text-gray-600">
              <SimpleNumberInputInline
                value={userAdjustedMargin}
                setValue={setUserAdjustedMargin}
                min={0}
                onBlur={handleMarginUpdateBlur}
                loading={loadingState === `margin`}
              />
            </td>
            <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
              {formatters.currency(
                proposalPrice.marginRate * proposalPrice.systemSize,
              )}
            </td>
          </tr>
          <SalesTaxRow
            salesTax={proposalPrice.salesTax}
            systemSizeInWatts={proposalPrice.systemSize}
          />
          <ExciseTaxRow
            exciseTaxRate={proposalPrice.generalExciseTaxRate}
            exciseTax={proposalPrice.generalExciseTax}
          />
          <tr key="total-cost" className="bg-white">
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800`}
            >
              Total Cost
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600`}
            >
              {formatters.pricePerWatt(
                proposalPrice.total / proposalPrice.systemSize,
              )}
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
            >
              {formatters.currency(currentTotalCost || 0)}
            </td>
          </tr>
        </tfoot>
      </PricingTableContainer>
    </>
  )
}

export default EnterpriseSystemPricing
